import { type CSSProperties, useState, useCallback, useRef } from 'react';
import { useAuth, sendPasswordResetForCurrentUser } from '@helios/auth-firebase-web';
import {
  qUserById,
  qUserProviderLink,
  qAppointmentProviders,
  qAppointmentProviderTypes,
  mapProviderRowToUI,
  type UserRow,
  type UserProviderLinkRow,
  type AppointmentProviderRow,
  type AppointmentProviderTypeRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { uploadToStorageScoped } from '../../lib/upload-to-storage';
import { requestPasswordReset, type PasswordResetGate } from './password-reset';

// ─── Styles ───

const pageStyle: CSSProperties = {
  maxWidth: 640,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-6, 24px)',
};

const sectionStyle: CSSProperties = {
  padding: 'var(--sp-5, 20px)',
  background: 'var(--bg-surface, #fff)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  borderRadius: 'var(--radius-lg, 14px)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-4, 16px)',
};

const sectionTitle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-semi, 600)',
  color: 'var(--text, #0f172a)',
  margin: 0,
};

const labelStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--text-muted, #64748b)',
  marginBottom: 4,
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  fontSize: 'var(--font-size-sm, 14px)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  outline: 'none',
  color: 'var(--text, #0f172a)',
  background: '#fff',
};

const btnStyle: CSSProperties = {
  padding: '8px 16px',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  borderRadius: 'var(--radius-md, 10px)',
  border: '1px solid var(--border, rgba(15,23,42,0.12))',
  background: '#fff',
  color: 'var(--text, #0f172a)',
  cursor: 'pointer',
};

const btnPrimaryStyle: CSSProperties = {
  ...btnStyle,
  background: 'var(--color-primary, #2563eb)',
  color: '#fff',
  border: 'none',
};

const btnDangerStyle: CSSProperties = {
  ...btnStyle,
  color: '#dc2626',
  borderColor: '#fecaca',
};

// ─── Component ───

export function ProfileAccountPage() {
  const { user, claims } = useAuth();
  const { db } = usePowerSyncDb();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uid = user?.uid ?? '';
  const email = user?.email ?? '';
  const orgId = claims?.activeOrg ?? '';
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const isAdmin = roles.includes('admin');

  // ─── Data queries ───
  const { data: userRows } = usePowerSyncQuery<UserRow>(qUserById(uid));
  const userRow = userRows?.[0] ?? null;

  const { data: linkRows } = usePowerSyncQuery<UserProviderLinkRow>(qUserProviderLink(orgId, uid));
  const linkRow = linkRows?.[0] ?? null;

  const { data: providerRows } = usePowerSyncQuery<AppointmentProviderRow>(qAppointmentProviders());
  const { data: typeRows } = usePowerSyncQuery<AppointmentProviderTypeRow>(
    qAppointmentProviderTypes(),
  );

  const providers = (providerRows ?? [])
    .filter((r) => r.active === 1)
    .map((r) => mapProviderRowToUI(r, typeRows ?? []));

  const linkedProvider = linkRow
    ? (providers.find((p) => p.id === linkRow.provider_id) ?? null)
    : null;

  // ─── Local state ───
  const [displayName, setDisplayName] = useState<string | null>(null);
  const effectiveDisplayName = displayName ?? userRow?.display_name ?? '';

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSending, setResetSending] = useState(false);
  const [toast, setToast] = useState('');

  // Survives re-renders so repeated clicks cannot start concurrent requests.
  const resetGateRef = useRef<PasswordResetGate>({ inFlight: false });

  // ─── Handlers ───

  /** Ensure user row exists, then return true. */
  const ensureUserRow = useCallback(async () => {
    if (!db || !uid || !email) return false;
    const now = new Date().toISOString();
    await db.execute(
      `INSERT OR IGNORE INTO users (id, email, display_name, photo_url, updated_at) VALUES (?, ?, ?, NULL, ?)`,
      [uid, email, user?.displayName ?? email.split('@')[0], now],
    );
    return true;
  }, [db, uid, email, user?.displayName]);

  const handleSaveDisplayName = useCallback(async () => {
    if (!db || !uid || !effectiveDisplayName.trim()) return;
    setSaving(true);
    try {
      await ensureUserRow();
      await db.execute('UPDATE users SET display_name = ?, updated_at = ? WHERE id = ?', [
        effectiveDisplayName.trim(),
        new Date().toISOString(),
        uid,
      ]);
      setToast('Display name saved');
      setTimeout(() => setToast(''), 2500);
    } finally {
      setSaving(false);
    }
  }, [db, uid, effectiveDisplayName, ensureUserRow]);

  const handleUploadPhoto = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!db || !file || !orgId || !uid) return;
      setUploading(true);
      try {
        await ensureUserRow();
        const result = await uploadToStorageScoped({
          orgId,
          scope: 'users',
          scopeId: uid,
          file,
          fileId: 'avatar',
          folder: 'avatar',
        });
        await db.execute('UPDATE users SET photo_url = ?, updated_at = ? WHERE id = ?', [
          result.downloadUrl,
          new Date().toISOString(),
          uid,
        ]);
        setToast('Photo updated');
        setTimeout(() => setToast(''), 2500);
      } catch {
        setToast('Upload failed');
        setTimeout(() => setToast(''), 3000);
      } finally {
        setUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [db, orgId, uid, ensureUserRow],
  );

  const handleRemovePhoto = useCallback(async () => {
    if (!db || !uid) return;
    await ensureUserRow();
    await db.execute('UPDATE users SET photo_url = ?, updated_at = ? WHERE id = ?', [
      null,
      new Date().toISOString(),
      uid,
    ]);
    setToast('Photo removed');
    setTimeout(() => setToast(''), 2500);
  }, [db, uid, ensureUserRow]);

  // PRD.303 — the reset target is always the authenticated Firebase account;
  // no email is passed in from this page.
  const handlePasswordReset = useCallback(async () => {
    setResetSending(true);

    const result = await requestPasswordReset({
      gate: resetGateRef.current,
      getEmail: () => email,
      send: sendPasswordResetForCurrentUser,
    });

    // `busy` means an earlier request is still in flight and owns the sending
    // state — leave it running rather than clearing the spinner early.
    if (result.status === 'busy') return;

    if (result.status === 'sent') {
      setResetSuccess(result.message);
      setResetError('');
    } else {
      setResetError(result.message);
    }
    setResetSending(false);
  }, [email]);

  const handleLinkProvider = useCallback(
    async (providerId: string) => {
      if (!db || !orgId || !uid) return;
      const existing = linkRow;
      if (existing) {
        // Update existing link
        await db.execute(
          'UPDATE user_provider_links SET provider_id = ?, updated_at = ? WHERE id = ?',
          [providerId, new Date().toISOString(), existing.id],
        );
      } else {
        // Create new link
        const id = crypto.randomUUID();
        await db.execute(
          `INSERT INTO user_provider_links (id, org_id, user_id, provider_id, status, created_at, updated_at) VALUES (?, ?, ?, ?, 'active', ?, ?)`,
          [id, orgId, uid, providerId, new Date().toISOString(), new Date().toISOString()],
        );
      }
      setToast('Provider linked');
      setTimeout(() => setToast(''), 2500);
    },
    [db, orgId, uid, linkRow],
  );

  const handleUnlinkProvider = useCallback(async () => {
    if (!db || !linkRow) return;
    await db.execute('DELETE FROM user_provider_links WHERE id = ?', [linkRow.id]);
    setToast('Provider unlinked');
    setTimeout(() => setToast(''), 2500);
  }, [db, linkRow]);

  // ─── Render helpers ───

  const photoUrl = userRow?.photo_url ?? null;
  const initials = effectiveDisplayName
    ? effectiveDisplayName
        .split(' ')
        .map((w) => w[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : (email?.[0]?.toUpperCase() ?? '?');

  return (
    <div style={pageStyle}>
      {/* Toast */}
      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            padding: '10px 20px',
            background: '#0f172a',
            color: '#fff',
            borderRadius: 'var(--radius-lg, 14px)',
            fontSize: 'var(--font-size-sm, 14px)',
            fontWeight: 'var(--font-weight-medium, 500)',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {toast}
        </div>
      )}

      {/* ─── Account Section ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Account</h2>

        {/* Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-4, 16px)' }}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt="Avatar"
              style={{
                width: 64,
                height: 64,
                borderRadius: 'var(--radius-full, 9999px)',
                objectFit: 'cover',
                border: '2px solid var(--border, rgba(15,23,42,0.12))',
              }}
            />
          ) : (
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 'var(--radius-full, 9999px)',
                background: 'var(--color-primary, #2563eb)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 20,
                fontWeight: 600,
                border: '2px solid var(--border, rgba(15,23,42,0.12))',
              }}
            >
              {initials}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleUploadPhoto}
            />
            <button
              style={btnStyle}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload Photo'}
            </button>
            {photoUrl && (
              <button style={btnDangerStyle} onClick={handleRemovePhoto}>
                Remove Photo
              </button>
            )}
          </div>
        </div>

        {/* Display Name */}
        <div>
          <div style={labelStyle}>Display Name</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              style={{ ...inputStyle, flex: 1 }}
              value={effectiveDisplayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
            />
            <button
              style={btnPrimaryStyle}
              onClick={handleSaveDisplayName}
              disabled={saving || !effectiveDisplayName.trim()}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Email */}
        <div>
          <div style={labelStyle}>Email</div>
          <input
            style={{ ...inputStyle, background: '#f8fafc', color: '#64748b' }}
            value={email}
            readOnly
          />
        </div>

        {/* Role */}
        {roles.length > 0 && (
          <div>
            <div style={labelStyle}>Role</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {roles.map((role) => (
                <span
                  key={role}
                  style={{
                    display: 'inline-block',
                    padding: '2px 10px',
                    borderRadius: 'var(--radius-full, 9999px)',
                    fontSize: 'var(--font-size-xs, 12px)',
                    fontWeight: 'var(--font-weight-medium, 500)',
                    background: role === 'admin' ? '#ede9fe' : '#f0fdf4',
                    color: role === 'admin' ? '#7c3aed' : '#16a34a',
                    border: `1px solid ${role === 'admin' ? '#ddd6fe' : '#bbf7d0'}`,
                    textTransform: 'capitalize',
                  }}
                >
                  {role}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ─── Security Section ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Security</h2>
        <div>
          <p
            style={{
              margin: '0 0 8px',
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'var(--text-muted, #64748b)',
            }}
          >
            {email
              ? `We'll send a reset link to ${email}.`
              : 'No email associated with this account.'}
          </p>
          {resetError && (
            <p
              style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm, 14px)', color: '#dc2626' }}
            >
              {resetError}
            </p>
          )}
          {resetSuccess && (
            <p
              style={{ margin: '0 0 8px', fontSize: 'var(--font-size-sm, 14px)', color: '#16a34a' }}
            >
              {resetSuccess}
            </p>
          )}
          <button style={btnStyle} onClick={handlePasswordReset} disabled={!email || resetSending}>
            {resetSending
              ? 'Sending...'
              : resetSuccess
                ? 'Send Again'
                : 'Send Password Reset Email'}
          </button>
        </div>
      </div>

      {/* ─── Clinical Identity Section ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Provider Link (optional)</h2>

        {/* Current status */}
        {linkedProvider ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '10px 14px',
              background: '#f0fdf4',
              borderRadius: 'var(--radius-md, 10px)',
              border: '1px solid #bbf7d0',
            }}
          >
            <span style={{ fontSize: 'var(--font-size-sm, 14px)', color: 'var(--text, #0f172a)' }}>
              Linked to: <strong>{linkedProvider.fullName}</strong>
              {linkedProvider.types.length > 0 && (
                <span style={{ color: 'var(--text-muted, #64748b)' }}>
                  {' '}
                  ({linkedProvider.types.join(', ')})
                </span>
              )}
            </span>
            {isAdmin && (
              <button style={btnDangerStyle} onClick={handleUnlinkProvider}>
                Unlink
              </button>
            )}
          </div>
        ) : (
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm, 14px)',
              color: 'var(--text-muted, #64748b)',
            }}
          >
            Not linked to a provider.
          </p>
        )}

        {/* Provider selector (admin only) */}
        {isAdmin && (
          <div>
            <div style={labelStyle}>{linkedProvider ? 'Change Provider' : 'Link Provider'}</div>
            {providers.length === 0 ? (
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm, 14px)',
                  color: 'var(--text-muted, #64748b)',
                }}
              >
                No providers yet.
              </p>
            ) : (
              <ProviderSelector
                providers={providers}
                currentProviderId={linkRow?.provider_id ?? null}
                onSelect={handleLinkProvider}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Provider Selector ───

function ProviderSelector({
  providers,
  currentProviderId,
  onSelect,
}: {
  providers: Array<{ id: string; fullName: string; types: string[] }>;
  currentProviderId: string | null;
  onSelect: (id: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = providers.filter(
    (p) =>
      p.fullName.toLowerCase().includes(search.toLowerCase()) ||
      p.types.some((t) => t.toLowerCase().includes(search.toLowerCase())),
  );

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={inputStyle}
        placeholder="Search providers..."
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && filtered.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            background: '#fff',
            border: '1px solid var(--border, rgba(15,23,42,0.12))',
            borderRadius: 'var(--radius-md, 10px)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxHeight: 200,
            overflowY: 'auto',
            zIndex: 10,
          }}
        >
          {filtered.map((p) => (
            <button
              key={p.id}
              onClick={() => {
                onSelect(p.id);
                setSearch('');
                setOpen(false);
              }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 14px',
                textAlign: 'left',
                border: 'none',
                background: p.id === currentProviderId ? '#f0f9ff' : 'transparent',
                cursor: 'pointer',
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--text, #0f172a)',
              }}
            >
              {p.fullName}
              {p.types.length > 0 && (
                <span style={{ color: 'var(--text-muted, #64748b)', marginLeft: 6 }}>
                  ({p.types.join(', ')})
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
